import React, { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import ConversationHistory from "./ConversationHistory";
import { getAgentBaseUrl } from "./conversationClient";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Link,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import AddCommentRoundedIcon from "@mui/icons-material/AddCommentRounded";
import HistoryRoundedIcon from "@mui/icons-material/HistoryRounded";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import CheckCircleOutlineRoundedIcon from "@mui/icons-material/CheckCircleOutlineRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import DescriptionRoundedIcon from "@mui/icons-material/DescriptionRounded";
import TableChartRoundedIcon from "@mui/icons-material/TableChartRounded";
import CameraAltRoundedIcon from "@mui/icons-material/CameraAltRounded";
import ShowChartRoundedIcon from "@mui/icons-material/ShowChartRounded";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import KeyboardArrowUpRoundedIcon from "@mui/icons-material/KeyboardArrowUpRounded";
import ImageRoundedIcon from "@mui/icons-material/ImageRounded";
import PersonRoundedIcon from "@mui/icons-material/PersonRounded";
import SmartToyRoundedIcon from "@mui/icons-material/SmartToyRounded";
import StopCircleRoundedIcon from "@mui/icons-material/StopCircleRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";

const BOLD_TEXT_COLOR = "rgba(232,78,14,0.75)"

function downloadExport({ filename, data, mimeType }) {
  const bytes = Uint8Array.from(atob(data), (c) => c.charCodeAt(0));
  const blob = new Blob([bytes], {
    type: mimeType || "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function GlowDivider() {
  return (
    <Box
      sx={{
        height: "1px",
        flexShrink: 0,
        background:
          "linear-gradient(90deg, transparent 0%, rgba(232,78,14,0.5) 28%, rgba(255,130,50,0.32) 55%, rgba(126,212,255,0.18) 80%, transparent 100%)",
        boxShadow: "0 0 8px rgba(232,78,14,0.14), 0 0 2px rgba(255,130,50,0.1)",
      }}
    />
  );
}

function formatContextSummary(viewContext) {
  if (!viewContext) return "Sin contexto";

  const parts = [
    viewContext.service,
    viewContext.module,
    viewContext.view,
    viewContext.entities?.region,
    viewContext.entities?.area,
    viewContext.entities?.centro,
  ].filter(Boolean);

  return parts.join(" / ");
}

function isTableRow(line) {
  const t = line.trim();
  return t.startsWith("|") && t.endsWith("|") && t.split("|").length > 2;
}

function isSeparatorRow(line) {
  const t = line.trim();
  if (!t.startsWith("|") || !t.endsWith("|")) return false;
  return t
    .slice(1, -1)
    .split("|")
    .every((cell) => /^[\s:-]+$/.test(cell));
}

function parseTableRow(line) {
  return line
    .trim()
    .slice(1, -1)
    .split("|")
    .map((cell) => cell.trim());
}

function parseMarkdownBlocks(markdown) {
  if (!markdown?.trim()) return [];

  const lines = markdown.replace(/\r\n/g, "\n").split("\n");
  const blocks = [];
  let paragraphLines = [];
  let listBlock = null;

  const flushParagraph = () => {
    if (!paragraphLines.length) return;
    blocks.push({
      type: "paragraph",
      lines: paragraphLines,
    });
    paragraphLines = [];
  };

  const flushList = () => {
    if (!listBlock?.items.length) return;
    blocks.push(listBlock);
    listBlock = null;
  };

  for (let index = 0; index < lines.length; index += 1) {
    const line = lines[index];

    if (/^```/.test(line.trim())) {
      flushParagraph();
      flushList();

      const language = line.trim().slice(3).trim();
      const codeLines = [];
      let cursor = index + 1;

      while (cursor < lines.length && !/^```/.test(lines[cursor].trim())) {
        codeLines.push(lines[cursor]);
        cursor += 1;
      }

      blocks.push({
        type: "code",
        language,
        content: codeLines.join("\n"),
      });

      index = cursor < lines.length ? cursor : lines.length;
      continue;
    }

    if (!line.trim()) {
      flushParagraph();
      flushList();
      continue;
    }

    const headingMatch = line.match(/^(#{1,6})\s+(.+)$/);
    if (headingMatch) {
      flushParagraph();
      flushList();
      blocks.push({
        type: "heading",
        level: headingMatch[1].length,
        content: headingMatch[2],
      });
      continue;
    }

    const quoteMatch = line.match(/^>\s?(.*)$/);
    if (quoteMatch) {
      flushParagraph();
      flushList();

      const quoteLines = [quoteMatch[1]];
      let cursor = index + 1;
      while (cursor < lines.length) {
        const nestedQuoteMatch = lines[cursor].match(/^>\s?(.*)$/);
        if (!nestedQuoteMatch) break;
        quoteLines.push(nestedQuoteMatch[1]);
        cursor += 1;
      }

      blocks.push({
        type: "blockquote",
        lines: quoteLines,
      });
      index = cursor - 1;
      continue;
    }

    const unorderedMatch = line.match(/^[-*+]\s+(.+)$/);
    if (unorderedMatch) {
      flushParagraph();
      if (!listBlock || listBlock.ordered) {
        flushList();
        listBlock = { type: "list", ordered: false, items: [] };
      }
      listBlock.items.push(unorderedMatch[1]);
      continue;
    }

    const orderedMatch = line.match(/^\d+\.\s+(.+)$/);
    if (orderedMatch) {
      flushParagraph();
      if (!listBlock || !listBlock.ordered) {
        flushList();
        listBlock = { type: "list", ordered: true, items: [] };
      }
      listBlock.items.push(orderedMatch[1]);
      continue;
    }

    if (isTableRow(line) && index + 1 < lines.length && isSeparatorRow(lines[index + 1])) {
      flushParagraph();
      flushList();
      const headers = parseTableRow(line);
      let cursor = index + 2;
      const rows = [];
      while (cursor < lines.length && isTableRow(lines[cursor])) {
        rows.push(parseTableRow(lines[cursor]));
        cursor += 1;
      }
      blocks.push({ type: "table", headers, rows });
      index = cursor - 1;
      continue;
    }

    flushList();
    paragraphLines.push(line);
  }

  flushParagraph();
  flushList();

  return blocks;
}

function renderInlineMarkdown(text, keyPrefix) {
  if (!text) return null;

  const parts = [];
  const pattern =
    /(\[([^\]]+)\]\((https?:\/\/[^\s)]+)\))|(`([^`]+)`)|(\*\*([^*]+)\*\*)|(__(.+?)__)|(\*([^*\n]+)\*)|(_([^_\n]+)_)/g;
  let cursor = 0;
  let match;
  let index = 0;

  while ((match = pattern.exec(text)) !== null) {
    if (match.index > cursor) {
      parts.push(text.slice(cursor, match.index));
    }

    if (match[1]) {
      parts.push(
        <Link
          key={`${keyPrefix}-link-${index}`}
          href={match[3]}
          target="_blank"
          rel="noreferrer"
          underline="hover"
          sx={{
            color: "#7ed4ff",
            fontWeight: 600,
          }}
        >
          {match[2]}
        </Link>
      );
    } else if (match[4]) {
      parts.push(
        <Box
          key={`${keyPrefix}-code-${index}`}
          component="code"
          sx={{
            px: 0.5,
            py: 0.1,
            borderRadius: 1,
            bgcolor: "rgba(255,255,255,0.14)",
            fontFamily: "monospace",
            fontSize: "0.86em",
          }}
        >
          {match[5]}
        </Box>
      );
    } else if (match[6] || match[8]) {
      parts.push(
        <Box
          key={`${keyPrefix}-strong-${index}`}
          component="strong"
          sx={{ fontWeight: 700, color:BOLD_TEXT_COLOR }}
        >
          {match[7] || match[9]}
        </Box>
      );
    } else if (match[10] || match[12]) {
      parts.push(
        <Box
          key={`${keyPrefix}-em-${index}`}
          component="em"
          sx={{ fontStyle: "italic" }}
        >
          {match[11] || match[13]}
        </Box>
      );
    }

    cursor = pattern.lastIndex;
    index += 1;
  }

  if (cursor < text.length) {
    parts.push(text.slice(cursor));
  }

  return parts.map((part, partIndex) =>
    typeof part === "string" ? (
      <Fragment key={`${keyPrefix}-text-${partIndex}`}>{part}</Fragment>
    ) : (
      part
    )
  );
}

function MarkdownMessage({ content }) {
  const blocks = useMemo(() => parseMarkdownBlocks(content), [content]);

  if (!blocks.length) return null;

  return (
    <Stack spacing={1.1} sx={{ userSelect: "text", WebkitUserSelect: "text" }}>
      {blocks.map((block, blockIndex) => {
        if (block.type === "heading") {
          return (
            <Typography
              key={`heading-${blockIndex}`}
              sx={{
                fontWeight: 700,
                fontSize: `${Math.max(1.05, 1.35 - block.level * 0.08)}rem`,
                lineHeight: 1.25,
              }}
            >
              {renderInlineMarkdown(block.content, `heading-${blockIndex}`)}
            </Typography>
          );
        }

        if (block.type === "blockquote") {
          return (
            <Box
              key={`quote-${blockIndex}`}
              sx={{
                pl: 1.5,
                py: 0.8,
                borderLeft: "3px solid rgba(126,212,255,0.55)",
                bgcolor: "rgba(255,255,255,0.05)",
                borderRadius: 1.5,
              }}
            >
              <Typography
                sx={{
                  fontSize: "0.9rem",
                  lineHeight: 1.65,
                  fontStyle: "italic",
                  opacity: 0.92,
                }}
              >
                {block.lines.map((line, lineIndex) => (
                  <Fragment key={`quote-line-${lineIndex}`}>
                    {renderInlineMarkdown(line, `quote-${blockIndex}-${lineIndex}`)}
                    {lineIndex < block.lines.length - 1 ? <br /> : null}
                  </Fragment>
                ))}
              </Typography>
            </Box>
          );
        }

        if (block.type === "code") {
          return (
            <Box
              key={`code-${blockIndex}`}
              component="pre"
              sx={{
                m: 0,
                px: 1.5,
                py: 1.25,
                borderRadius: 2,
                overflowX: "auto",
                bgcolor: "rgba(8,12,20,0.72)",
                border: "1px solid rgba(255,255,255,0.08)",
                fontFamily: "monospace",
                fontSize: "0.8rem",
                lineHeight: 1.6,
              }}
            >
              {block.language ? `${block.language}\n` : ""}
              {block.content}
            </Box>
          );
        }

        if (block.type === "list") {
          const ListTag = block.ordered ? "ol" : "ul";
          return (
            <Box
              key={`list-${blockIndex}`}
              component={ListTag}
              sx={{
                my: 0,
                pl: 2.8,
                display: "grid",
                gap: 0.7,
                "& li::marker": {
                  color: "rgba(126,212,255,0.95)",
                },
              }}
            >
              {block.items.map((item, itemIndex) => (
                <Box
                  key={`list-item-${itemIndex}`}
                  component="li"
                  sx={{
                    pl: 0.3,
                    fontSize: "0.93rem",
                    lineHeight: 1.65,
                  }}
                >
                  {renderInlineMarkdown(item, `list-${blockIndex}-${itemIndex}`)}
                </Box>
              ))}
            </Box>
          );
        }

        if (block.type === "table") {
          return (
            <Box
              key={`table-${blockIndex}`}
              sx={{
                overflowX: "auto",
                borderRadius: 1.5,
                border: "1px solid rgba(255,255,255,0.09)",
                "&::-webkit-scrollbar": { height: 6 },
                "&::-webkit-scrollbar-thumb": {
                  bgcolor: "rgba(255,255,255,0.12)",
                  borderRadius: 999,
                },
              }}
            >
              <Box
                component="table"
                sx={{
                  borderCollapse: "collapse",
                  width: "100%",
                  fontSize: "0.87rem",
                  lineHeight: 1.55,
                  "& th, & td": {
                    px: 1.4,
                    py: 0.75,
                    borderBottom: "1px solid rgba(255,255,255,0.07)",
                    borderRight: "1px solid rgba(255,255,255,0.05)",
                    verticalAlign: "top",
                    "&:last-child": { borderRight: "none" },
                  },
                  "& thead tr": {
                    bgcolor: "rgba(126,212,255,0.08)",
                  },
                  "& th": {
                    fontWeight: 700,
                    color: "#8cdfff",
                    whiteSpace: "nowrap",
                    borderBottom: "1px solid rgba(126,212,255,0.2)",
                  },
                  "& tbody tr:nth-of-type(even)": {
                    bgcolor: "rgba(255,255,255,0.025)",
                  },
                  "& tbody tr:hover": {
                    bgcolor: "rgba(126,212,255,0.06)",
                  },
                  "& tbody tr:last-child td": {
                    borderBottom: "none",
                  },
                }}
              >
                <Box component="thead">
                  <Box component="tr">
                    {block.headers.map((header, i) => (
                      <Box key={i} component="th">
                        {renderInlineMarkdown(header, `table-${blockIndex}-h-${i}`)}
                      </Box>
                    ))}
                  </Box>
                </Box>
                <Box component="tbody">
                  {block.rows.map((row, rowIndex) => (
                    <Box key={rowIndex} component="tr">
                      {row.map((cell, cellIndex) => (
                        <Box key={cellIndex} component="td">
                          {renderInlineMarkdown(cell, `table-${blockIndex}-r${rowIndex}-c${cellIndex}`)}
                        </Box>
                      ))}
                    </Box>
                  ))}
                </Box>
              </Box>
            </Box>
          );
        }

        return (
          <Typography
            key={`paragraph-${blockIndex}`}
            sx={{
              fontSize: "0.93rem",
              lineHeight: 1.7,
              wordBreak: "break-word",
            }}
          >
            {block.lines.map((line, lineIndex) => (
              <Fragment key={`paragraph-line-${lineIndex}`}>
                {renderInlineMarkdown(line, `paragraph-${blockIndex}-${lineIndex}`)}
                {lineIndex < block.lines.length - 1 ? <br /> : null}
              </Fragment>
            ))}
          </Typography>
        );
      })}
    </Stack>
  );
}

function getProgressTitle(event) {
  return event?.title || event?.label || "Preparando respuesta";
}

function getProgressDetail(event) {
  return event?.detail || "";
}

function ProgressStatusIcon({ status }) {
  if (status === "done") {
    return <CheckCircleOutlineRoundedIcon sx={{ fontSize: 16, color: "#7ee0a8" }} />;
  }
  if (status === "warning") {
    return <WarningAmberRoundedIcon sx={{ fontSize: 16, color: "#ffcc9d" }} />;
  }
  return <CircularProgress size={14} thickness={5} sx={{ color: "#8cdfff" }} />;
}

function AgentProgress({ events }) {
  const [expanded, setExpanded] = useState(false);
  const visibleEvents = Array.isArray(events) ? events.filter(Boolean) : [];

  if (!visibleEvents.length) return null;

  const visibleLatest = visibleEvents[visibleEvents.length - 1];

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: "92%",
        mb: 0.2,
        borderRadius: 2.2,
        bgcolor: "rgba(126,212,255,0.065)",
        border: "1px solid rgba(126,212,255,0.12)",
        overflow: "hidden",
      }}
    >
      <Button
        type="button"
        onClick={() => setExpanded((current) => !current)}
        endIcon={expanded ? <KeyboardArrowUpRoundedIcon /> : <KeyboardArrowDownRoundedIcon />}
        sx={{
          width: "100%",
          minHeight: 0,
          px: 1.2,
          py: 0.85,
          color: "#dff3ff",
          justifyContent: "space-between",
          textTransform: "none",
          borderRadius: 0,
          "& .MuiButton-endIcon": {
            ml: 1,
            color: "rgba(223,243,255,0.72)",
          },
        }}
      >
        <Stack direction="row" spacing={0.9} alignItems="center" sx={{ minWidth: 0 }}>
          <ProgressStatusIcon status={visibleLatest?.status} />
          <Box sx={{ minWidth: 0, textAlign: "left" }}>
            <Typography
              sx={{
                fontSize: "0.78rem",
                fontWeight: 700,
                lineHeight: 1.25,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {getProgressTitle(visibleLatest)}
            </Typography>
            {getProgressDetail(visibleLatest) && (
              <Typography
                sx={{
                  mt: 0.15,
                  fontSize: "0.72rem",
                  lineHeight: 1.35,
                  color: "rgba(223,243,255,0.72)",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {getProgressDetail(visibleLatest)}
              </Typography>
            )}
          </Box>
        </Stack>
      </Button>

      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <Stack spacing={0.85} sx={{ px: 1.35, pb: 1.1 }}>
          {visibleEvents.map((event, index) => (
            <Stack
              key={`${event.type || "progress"}-${index}-${event.createdAt || ""}`}
              direction="row"
              spacing={0.8}
              sx={{ alignItems: "flex-start" }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 0.3,
                  pt: 0.1,
                  flexShrink: 0,
                }}
              >
                <Typography
                  sx={{
                    fontSize: "0.62rem",
                    fontWeight: 800,
                    color: "rgba(126,212,255,0.55)",
                    lineHeight: 1,
                    letterSpacing: "0.02em",
                    minWidth: "1.1rem",
                    textAlign: "center",
                  }}
                >
                  {index + 1}
                </Typography>
                <ProgressStatusIcon status={event.status} />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  sx={{
                    fontSize: "0.75rem",
                    fontWeight: 700,
                    color: "#edf8ff",
                    lineHeight: 1.3,
                  }}
                >
                  {getProgressTitle(event)}
                </Typography>
                {getProgressDetail(event) && (
                  <Typography
                    sx={{
                      mt: 0.15,
                      fontSize: "0.71rem",
                      lineHeight: 1.45,
                      color: "rgba(223,243,255,0.72)",
                      wordBreak: "break-word",
                    }}
                  >
                    {getProgressDetail(event)}
                  </Typography>
                )}
              </Box>
            </Stack>
          ))}
        </Stack>
      </Collapse>
    </Box>
  );
}

function MessageCard({ message }) {
  const isUser = message.role === "user";
  const hasError = message.status === "error";
  const sourceLabelPrefix = message.role === "assistant" ? "Base" : "Origen";
  const attachment = message.metadata?.attachment || null;
  const toolEvents = Array.isArray(message.metadata?.events) ? message.metadata.events : [];
  const warnings = Array.isArray(message.metadata?.warnings) ? message.metadata.warnings : [];

  const allSources = Array.isArray(message.sources) ? message.sources : [];
  const docSources = allSources.filter((s) => s.type !== "excel_export");
  const excelFromSources = allSources.filter((s) => s.type === "excel_export");
  const excelFromMetadata = Array.isArray(message.metadata?.exports) ? message.metadata.exports : [];
  const excelExports = [...excelFromSources, ...excelFromMetadata];

  return (
    <Stack
      direction={isUser ? "row-reverse" : "row"}
      spacing={1}
      sx={{
        alignSelf: isUser ? "flex-end" : "flex-start",
        width: "100%",
        maxWidth: "100%",
        userSelect: "text",
        WebkitUserSelect: "text",
      }}
    >
      <Avatar
        sx={{
          width: 30,
          height: 30,
          mt: 0.3,
          flexShrink: 0,
          bgcolor: isUser ? "rgba(232,78,14,0.14)" : "rgba(126,212,255,0.1)",
          color: isUser ? "#ffb085" : "#8cdfff",
          border: `1px solid ${isUser ? "rgba(232,78,14,0.22)" : "rgba(126,212,255,0.16)"}`,
        }}
      >
        {isUser
          ? <PersonRoundedIcon sx={{ fontSize: 16 }} />
          : <SmartToyRoundedIcon sx={{ fontSize: 16 }} />}
      </Avatar>

      <Box
        sx={{
          width: "calc(100% - 38px)",
          display: "flex",
          flexDirection: "column",
          alignItems: isUser ? "flex-end" : "flex-start",
          gap: 0.55,
        }}
      >
        {!isUser && toolEvents.length > 0 && <AgentProgress events={toolEvents} />}

        <Box
          sx={{
            maxWidth: "92%",
            px: 1.4,
            py: 1.05,
            borderRadius: isUser ? "14px 14px 4px 14px" : "4px 14px 14px 14px",
            color: "#eef4ff",
            ...(isUser
              ? {
                  bgcolor: "rgba(232,78,14,0.14)",
                  border: "1px solid rgba(232,78,14,0.22)",
                }
              : {
                  bgcolor: "rgba(255,255,255,0.04)",
                  border: "1px solid rgba(255,255,255,0.07)",
                  //borderLeft: "2.5px solid rgba(255,120,40,0.45)",
                }),
            ...(hasError && { borderColor: "rgba(255,90,90,0.35)", bgcolor: "rgba(255,60,60,0.06)" }),
            userSelect: "text",
            WebkitUserSelect: "text",
          }}
        >
          {message.content ? (
            <MarkdownMessage content={message.content} />
          ) : (
            <Typography sx={{ fontSize: "0.9rem", opacity: 0.45, fontStyle: "italic" }}>
              {isUser ? "" : "Escribiendo..."}
            </Typography>
          )}
        </Box>

        {hasError && (
          <Typography sx={{ px: 0.2, fontSize: "0.7rem", color: "rgba(255,130,130,0.75)", letterSpacing: "0.01em" }}>
            La respuesta terminó con incidencias
          </Typography>
        )}

        {attachment?.name && (
          <Chip
            size="small"
            icon={<ImageRoundedIcon />}
            label={attachment.name}
            sx={{
              maxWidth: "100%",
              bgcolor: "rgba(255,255,255,0.05)",
              color: "#cce0f5",
              border: "1px solid rgba(255,255,255,0.08)",
              "& .MuiChip-label": { display: "block", overflow: "hidden", textOverflow: "ellipsis" },
              "& .MuiChip-icon": { color: "#8cdfff" },
            }}
          />
        )}

        {!isUser && warnings.length > 0 && (
          <Stack spacing={0.35} sx={{ width: "100%", maxWidth: "92%" }}>
            {warnings.slice(-2).map((warning, index) => (
              <Typography key={`warning-${index}`} sx={{ fontSize: "0.72rem", color: "rgba(255,190,120,0.8)" }}>
                {warning}
              </Typography>
            ))}
          </Stack>
        )}

        {docSources.length > 0 && (
          <Stack direction="row" flexWrap="wrap" gap={0.6}>
            {docSources.map((source, index) => {
              const chipLabel = source.title || source.id || `${sourceLabelPrefix} ${index + 1}`;
              const chip = (
                <Chip
                  key={`${chipLabel}-${index}`}
                  size="small"
                  icon={<DescriptionRoundedIcon />}
                  label={chipLabel}
                  sx={{
                    maxWidth: "100%",
                    bgcolor: "rgba(255,255,255,0.05)",
                    color: "#cce0f5",
                    border: "1px solid rgba(255,255,255,0.08)",
                    "& .MuiChip-label": { display: "block", overflow: "hidden", textOverflow: "ellipsis" },
                    "& .MuiChip-icon": { color: "#8cdfff" },
                  }}
                />
              );

              if (!source.excerpt && !source.filePath) return chip;

              return (
                <Tooltip
                  key={`${chipLabel}-${index}`}
                  title={
                    <Box sx={{ py: 0.5 }}>
                      {source.filePath && <Typography sx={{ fontSize: "0.72rem", fontWeight: 700 }}>{source.filePath}</Typography>}
                      {source.excerpt && <Typography sx={{ fontSize: "0.74rem", opacity: 0.88 }}>{source.excerpt}</Typography>}
                    </Box>
                  }
                  arrow
                  placement="top-start"
                >
                  {chip}
                </Tooltip>
              );
            })}
          </Stack>
        )}

        {excelExports.length > 0 && (
          <Stack direction="row" flexWrap="wrap" gap={0.6} sx={{ mt: docSources.length > 0 ? 0.3 : 0 }}>
            {excelExports.map((exp, idx) => (
              <Chip
                key={`excel-${idx}`}
                size="small"
                icon={<TableChartRoundedIcon />}
                label={exp.filename}
                onClick={() => downloadExport(exp)}
                clickable
                sx={{
                  bgcolor: "rgba(100,200,100,0.08)",
                  color: "#8bd48b",
                  border: "1px solid rgba(100,200,100,0.2)",
                  "& .MuiChip-icon": { color: "#8bd48b" },
                  "&:hover": { bgcolor: "rgba(100,200,100,0.16)" },
                }}
              />
            ))}
          </Stack>
        )}
      </Box>
    </Stack>
  );
}

export default function AssistantPanel({
  open,
  messages,
  input,
  onInputChange,
  onClose,
  onSend,
  onCancel,
  onPasteImage,
  onClearPendingImage,
  onNewChat,
  pendingImageName,
  autoScreenshotEnabled,
  onAutoScreenshotEnabledChange,
  includeGraphData,
  onIncludeGraphDataChange,
  requestPhase,
  requestNotice,
  requestError,
  webhookConfigured,
  viewContext,
  aiWebhookUrl,
  onRestoreConversation,
}) {
  const listRef = useRef(null);
  const requestActive = requestPhase !== "idle";
  const [showHistory, setShowHistory] = useState(false);
  const agentBaseUrl = getAgentBaseUrl(aiWebhookUrl);
  const canSubmit = Boolean(input.trim() || pendingImageName);

  const MIN_WIDTH_PX = 464;
  const [panelWidth, setPanelWidth] = useState(MIN_WIDTH_PX);
  const dragRef = useRef({ active: false, startX: 0, startWidth: 0 });

  const handleResizeMouseDown = useCallback(
    (e) => {
      e.preventDefault();
      dragRef.current = { active: true, startX: e.clientX, startWidth: panelWidth };
      document.body.style.cursor = "col-resize";
      //document.body.style.userSelect = "none";
    },
    [panelWidth]
  );

  useEffect(() => {
    const onMove = (e) => {
      if (!dragRef.current.active) return;
      const delta = dragRef.current.startX - e.clientX;
      const maxWidth = Math.floor(window.innerWidth * 0.6);
      const next = Math.max(MIN_WIDTH_PX, Math.min(maxWidth, dragRef.current.startWidth + delta));
      setPanelWidth(next);
    };
    const onUp = () => {
      if (!dragRef.current.active) return;
      dragRef.current.active = false;
      document.body.style.cursor = "";
      //document.body.style.userSelect = "";
    };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
    return () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
    };
  }, []);


  const handleInputKeyDown = (event) => {
    if (event.key !== "Enter" || event.shiftKey || event.nativeEvent?.isComposing) {
      return;
    }
    if (requestActive || !canSubmit) return;
    event.preventDefault();
    onSend(event);
  };

  const handleInputPaste = (event) => {
    const items = Array.from(event.clipboardData?.items || []);
    const imageItem = items.find((item) => item.type?.startsWith("image/"));

    if (!imageItem) return;

    const file = imageItem.getAsFile();
    if (!file) return;

    event.preventDefault();
    onPasteImage(file);
  };

  useEffect(() => {
    if (!listRef.current) return;
    listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages, open]);

  return (
    <Paper
      data-ai-exclude-capture="true"
      elevation={0}
      sx={{
        position: "fixed",
        top: { xs: 8, sm: 8 },
        right: { xs: 8, sm: 4 },
        bottom: { xs: 8, sm: 8 },
        width: { xs: "calc(100vw - 16px)", sm: `${panelWidth}px` },
        maxWidth: { xs: "100vw", sm: "60vw" },
        minWidth: { sm: `${MIN_WIDTH_PX}px` },
        zIndex: 1499,
        display: "flex",
        flexDirection: "column",
        color: "#eef4ff",
        overflow: "hidden",
        borderRadius: { xs: 4, sm: "14px" },
        background:
          "linear-gradient(180deg, rgba(15,20,31,0.98) 0%, rgba(10,14,22,0.96) 100%)",
        border: "1px solid rgba(255,255,255,0.08)",
        boxShadow:
          "0 16px 34px rgba(0,0,0,0.24), 0 0 22px rgba(126,212,255,0.06), 0 0 14px rgba(232,78,14,0.06)",
        transform: open ? "translateX(0)" : "translateX(calc(100% + 24px))",
        opacity: open ? 1 : 0,
        transition: "transform 240ms ease, opacity 240ms ease",
        pointerEvents: open ? "auto" : "none",
        userSelect: "text",
        WebkitUserSelect: "text",
      }}
    >
      {/* Resize handle — left edge, desktop only */}
      <Box
        onMouseDown={handleResizeMouseDown}
        sx={{
          display: { xs: "none", sm: "flex" },
          alignItems: "center",
          justifyContent: "center",
          position: "absolute",
          left: 0,
          top: 0,
          bottom: 0,
          width: 10,
          cursor: "col-resize",
          zIndex: 3,
          "&:hover .resize-bar": {
            bgcolor: "rgba(126,212,255,0.45)",
          },
        }}
      >
        <Box
          className="resize-bar"
          sx={{
            width: "3px",
            height: 48,
            borderRadius: "3px",
            bgcolor: "rgba(126,212,255,0.14)",
            transition: "background-color 160ms ease",
            pointerEvents: "none",
          }}
        />
      </Box>
        {/* ── Header ── */}
        <Box
          sx={{
            px: 2,
            pt: 1.5,
            pb: 1.3,
            background:
              "radial-gradient(ellipse at top left, rgba(232,78,14,0.1) 0%, transparent 55%), radial-gradient(ellipse at top right, rgba(126,212,255,0.06) 0%, transparent 50%)",
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
            <AutoAwesomeRoundedIcon
              sx={{ color: "#ff9154", fontSize: 20, flexShrink: 0 }}
            />
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: "0.97rem",
                letterSpacing: "0.01em",
                flex: 1,
                minWidth: 0,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              Asistente IA
            </Typography>
            <Tooltip title={showHistory ? "Volver al chat" : "Historial"}>
              <IconButton
                size="small"
                onClick={() => setShowHistory((v) => !v)}
                sx={{
                  color: showHistory ? "#ff9154" : "rgba(255,255,255,0.5)",
                  p: 0.65,
                  "&:hover": { color: "#fff", bgcolor: "rgba(255,255,255,0.07)" },
                }}
              >
                <HistoryRoundedIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Nuevo chat">
              <span>
                <IconButton
                  size="small"
                  onClick={onNewChat}
                  disabled={requestActive}
                  sx={{
                    color: "rgba(255,255,255,0.5)",
                    p: 0.65,
                    "&:hover": { color: "#fff", bgcolor: "rgba(255,255,255,0.07)" },
                    "&.Mui-disabled": { color: "rgba(255,255,255,0.18)" },
                  }}
                >
                  <AddCommentRoundedIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </span>
            </Tooltip>
            <IconButton
              size="small"
              onClick={onClose}
              sx={{
                color: "rgba(255,255,255,0.5)",
                p: 0.65,
                "&:hover": { color: "#fff", bgcolor: "rgba(255,255,255,0.07)" },
              }}
            >
              <CloseRoundedIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Stack>

          <Typography
            sx={{
              mt: 0.45,
              ml: "28px",
              fontSize: "0.7rem",
              color: "rgba(255,255,255,0.3)",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {formatContextSummary(viewContext)}
          </Typography>
        </Box>

        <GlowDivider />

        {showHistory ? (
          <ConversationHistory
            agentBaseUrl={agentBaseUrl}
            currentService={viewContext?.service}
            onRestore={(convId, convMessages) => {
              onRestoreConversation?.(convId, convMessages);
              setShowHistory(false);
            }}
            onClose={() => setShowHistory(false)}
          />
        ) : (
          <Box
            ref={listRef}
            sx={{
              flex: 1,
              overflowY: "auto",
              px: 2,
              py: 2,
              display: "flex",
              flexDirection: "column",
              gap: 1.6,
              userSelect: "text",
              WebkitUserSelect: "text",
              background:
                "radial-gradient(circle at top right, rgba(126,212,255,0.06), transparent 28%), radial-gradient(circle at bottom left, rgba(232,78,14,0.08), transparent 30%)",
              "&::-webkit-scrollbar": { width: 8 },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "rgba(255,255,255,0.12)",
                borderRadius: 999,
              },
            }}
          >
            {messages.length ? (
              messages.map((message) => <MessageCard key={message.id} message={message} />)
            ) : (
              <Box
                sx={{
                  m: "auto 0",
                  p: 2,
                  borderRadius: 3,
                  bgcolor: "rgba(255,255,255,0.04)",
                  border: "1px solid rgba(255,255,255,0.06)",
                }}
              >
                <Typography sx={{ fontWeight: 600, mb: 0.8 }}>
                  El chat ya está contextualizado
                </Typography>
                <Typography sx={{ opacity: 0.74, fontSize: "0.9rem", lineHeight: 1.65 }}>
                  El asistente considera la ruta actual y el módulo activo. Por defecto
                  adjunta una captura automática y también acepta imágenes pegadas manualmente.
                </Typography>
              </Box>
            )}
          </Box>
        )}

        <GlowDivider />

        {/* ── Footer ── */}
        <Box
          component="form"
          onSubmit={onSend}
          sx={{
            px: 1.6,
            pt: 1.4,
            pb: 1.4,
            display: "flex",
            flexDirection: "column",
            gap: 0.9,
            bgcolor: "rgba(6,10,17,0.5)",
          }}
        >
          {!webhookConfigured && (
            <Alert severity="warning" sx={{ borderRadius: 2, fontSize: "0.78rem", py: 0.5 }}>
              Configura <code>aiWebhookUrl</code> para habilitar el asistente.
            </Alert>
          )}
          {requestNotice && (
            <Alert severity="info" sx={{ borderRadius: 2, fontSize: "0.78rem", py: 0.5 }}>
              {requestNotice}
            </Alert>
          )}
          {requestError && (
            <Alert severity="error" sx={{ borderRadius: 2, fontSize: "0.78rem", py: 0.5 }}>
              {requestError}
            </Alert>
          )}

          <TextField
            value={input}
            onChange={(event) => onInputChange(event.target.value)}
            onKeyDown={handleInputKeyDown}
            onPaste={handleInputPaste}
            multiline
            minRows={2}
            maxRows={6}
            placeholder={requestActive ? "Generando respuesta..." : "Escribe tu pregunta..."}
            variant="outlined"
            fullWidth
            disabled={!webhookConfigured}
            inputProps={{ readOnly: requestActive }}
            InputProps={{
              endAdornment: requestActive ? (
                <InputAdornment
                  position="end"
                  sx={{ alignSelf: "flex-end", pb: 0.5, flexShrink: 0 }}
                >
                  <Tooltip title="Detener generación">
                    <IconButton
                      size="small"
                      onClick={onCancel}
                      sx={{
                        color: "rgba(255,120,50,0.8)",
                        p: 0.55,
                        "&:hover": {
                          bgcolor: "rgba(232,78,14,0.12)",
                          color: "#ff9154",
                        },
                      }}
                    >
                      <StopCircleRoundedIcon sx={{ fontSize: 20 }} />
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              ) : null,
              sx: {
                borderRadius: "12px",
                bgcolor: "rgba(10,15,24,0.7)",
                color: "#eef4ff",
                alignItems: "flex-start",
                border: "1px solid rgba(232,78,14,0.22)",
                transition: "border-color 200ms ease, box-shadow 200ms ease",
                "&:hover:not(.Mui-disabled)": {
                  border: "1px solid rgba(232,78,14,0.4)",
                },
                "&.Mui-focused": {
                  border: "1px solid rgba(232,78,14,0.6)",
                  boxShadow:
                    "0 0 0 3px rgba(232,78,14,0.07), 0 0 16px rgba(232,78,14,0.05)",
                },
                "&.Mui-disabled": {
                  border: "1px solid rgba(255,255,255,0.05)",
                  bgcolor: "rgba(10,15,24,0.4)",
                },
                "& textarea::placeholder": {
                  color: requestActive
                    ? "rgba(255,120,50,0.45)"
                    : "rgba(200,220,245,0.32)",
                  opacity: 1,
                  fontStyle: requestActive ? "italic" : "normal",
                },
              },
            }}
          />

          {pendingImageName && (
            <Chip
              size="small"
              icon={<ImageRoundedIcon />}
              label={pendingImageName}
              onDelete={onClearPendingImage}
              sx={{
                alignSelf: "flex-start",
                maxWidth: "100%",
                bgcolor: "rgba(255,255,255,0.05)",
                color: "#cce0f5",
                border: "1px solid rgba(255,255,255,0.08)",
                "& .MuiChip-label": { overflow: "hidden", textOverflow: "ellipsis" },
                "& .MuiChip-icon": { color: "#8cdfff" },
                "& .MuiChip-deleteIcon": { color: "rgba(255,255,255,0.55)" },
              }}
            />
          )}

          <Box sx={{ display: "flex", flexWrap: "wrap", gap: "2px 12px", alignItems: "center" }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={autoScreenshotEnabled}
                  onChange={(event) => onAutoScreenshotEnabledChange(event.target.checked)}
                  size="small"
                  sx={{
                    color: "rgba(255,255,255,0.2)",
                    p: "3px",
                    "&.Mui-checked": { color: "#ff9154" },
                    "& .MuiSvgIcon-root": { fontSize: 15 },
                  }}
                />
              }
              label={
                <Box sx={{ display: "flex", alignItems: "center", gap: "3px" }}>
                  <CameraAltRoundedIcon sx={{ fontSize: "0.8rem", opacity: 0.45 }} />
                  <span>Captura de pantalla</span>
                </Box>
              }
              sx={{
                m: 0,
                gap: 0.3,
                "& .MuiFormControlLabel-label": {
                  fontSize: "0.73rem",
                  color: "rgba(200,220,245,0.45)",
                  letterSpacing: "0.01em",
                },
              }}
            />

            <Tooltip
              arrow
              placement="top"
              title={
                <Box sx={{ maxWidth: 280, p: 0.5 }}>
                  <Typography sx={{ fontSize: "0.72rem", mb: 0.8, color: "#cce0f5" }}>
                    Añade información granular <strong>por jaula y por día</strong> correspondiente al <strong>gráfico de Tasa de Alimentación</strong>.
                  </Typography>
                </Box>
              }
            >

            <FormControlLabel
              control={
                
                <Checkbox
                  checked={includeGraphData}
                  onChange={(event) => onIncludeGraphDataChange(event.target.checked)}
                  size="small"
                  sx={{
                    color: "rgba(255,255,255,0.2)",
                    p: "3px",
                    "&.Mui-checked": { color: "#ff9154" },
                    "& .MuiSvgIcon-root": { fontSize: 15 },
                  }}
                />
              }
              label={
                <Box sx={{ display: "flex", alignItems: "center", gap: "3px" }}>
                  <ShowChartRoundedIcon sx={{ fontSize: "0.8rem", opacity: 0.45 }} />
                  <span>Series temporales</span>
                </Box>
              }
              sx={{
                m: 0,
                gap: 0.3,
                "& .MuiFormControlLabel-label": {
                  fontSize: "0.73rem",
                  color: "rgba(200,220,245,0.45)",
                  letterSpacing: "0.01em",
                },
              }}
            />

            </Tooltip>
          </Box>

          <Typography
            sx={{
              fontSize: "0.67rem",
              color: "rgba(255,255,255,0.2)",
              lineHeight: 1.45,
              mt: -0.3,
            }}
          >
            La IA puede cometer errores. Verifica resultados importantes.{" "}
            <Tooltip
              arrow
              placement="top"
              title={
                <Box sx={{ maxWidth: 280, p: 0.5 }}>
                  <Typography sx={{ fontSize: "0.72rem", fontWeight: 600, mb: 0.8, color: "#cce0f5" }}>
                    Este servicio <strong style={{color: "#f58a67"}}>hace uso</strong> de un proveedor de inteligencia artifical <strong style={{color: "#f58a67"}} >externo a ITG</strong> al que se le comparte parte de la información procesada.
                  </Typography>
                  <Typography sx={{ fontSize: "0.72rem", fontWeight: 600, mb: 0.8, color: "#cce0f5" }}>
                    ¿Qué información es compartida?
                  </Typography>
                  <Box component="ul" sx={{ m: 0, pl: 2, display: "flex", flexDirection: "column", gap: 0.6 }}>
                    <Box component="li" sx={{ fontSize: "0.7rem", color: "rgba(200,225,255,0.85)" }}>
                      <strong style={{color: "#f58a67"}}>Captura de pantalla y mensajes del usuario</strong>: se envían completos al modelo externo.
                    </Box>
                    <Box component="li" sx={{ fontSize: "0.7rem", color: "rgba(200,225,255,0.85)" }}>
                      <strong style={{color: "#f58a67"}}>Contexto del sitio</strong> (servicio, centro, período): se envía al modelo externo.
                    </Box>
                    <Box component="li" sx={{ fontSize: "0.7rem", color: "rgba(200,225,255,0.85)" }}>
                      <strong style={{color: "#f58a67"}}>Datos operacionales</strong> (métricas y data cruda): se analizan localmente en servidores de ITG. El modelo externo solo recibe los <strong>resultados calculados</strong>, no el dataset completo.
                    </Box>
                  </Box>
                  <Typography sx={{ fontSize: "0.67rem", color: "rgba(200,225,255,0.5)", mt: 1 }}>
                    Incluye información sensible o confidencial en los mensajes bajo tu responsabilidad.
                  </Typography>
                </Box>
              }
            >
              <Box
                component="span"
                sx={{
                  cursor: "help",
                  borderBottom: "1px dotted rgba(255,255,255,0.25)",
                  color: "rgba(200,220,245,0.32)",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "2px",
                }}
              >
                <InfoOutlinedIcon sx={{ fontSize: "0.7rem", verticalAlign: "middle" }} />
                Privacidad
              </Box>
            </Tooltip>
          </Typography>
        </Box>
    </Paper>
  );
}
